import { ReloadOutlined } from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type IReportPropertyValueLanguage,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { Button, Divider, Popconfirm, Space, Spin, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import {
  AutomatProgressBar,
  type IAutomatProgress,
} from '../fragments/automat-progress-bar';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useAutomatDocumentTitle } from '../hooks/use-automat-document-title';
import { type AppDispatch, type RootState } from '../reducers';
import { updateValueNames } from '../reducers/property/reducer';
import { reportPropertyValueLanguagesLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

interface DataType extends IReportPropertyValueLanguage {
  key: string;
}

type FilterMode = 'all' | 'numeric' | 'non-numeric' | 'dimensions';

const isNumericCode = (code: string): boolean =>
  /^[A-Z_]+[\s][\d.,\s]+$/i.test(code);

const isDimensionCode = (code: string): boolean => /[\s](MM|CM)$/i.test(code);

const deriveNameFromCode = (code: string): string =>
  code
    .replace(/^[A-Z_]+\s/i, '')
    .trim()
    .toLowerCase();

function openGoogleTranslateLink(
  fromLanguageCode: string,
  toLanguageCode: string,
  text: string,
): void {
  const gtLink = new URL('https://translate.google.com/');
  gtLink.searchParams.append('sl', fromLanguageCode.toLowerCase());
  gtLink.searchParams.append('tl', toLanguageCode.toLowerCase());
  gtLink.searchParams.append('text', text.trim());
  gtLink.searchParams.append('op', 'translate');

  window.open(gtLink.toString(), '_blank');
}

const SOURCE_LANGUAGE_CODE_AUTO_DETECT = 'auto';

function resolveAutomatSource(record: DataType): ITranslation {
  const [translationName] = record.translation.names.filter(notEmpty);

  if (translationName) {
    return translationName;
  }

  return {
    code: SOURCE_LANGUAGE_CODE_AUTO_DETECT,
    value: deriveNameFromCode(record.propertyValue.code),
  };
}

async function startAutomat(
  dataSource: DataType[],
  dispatch: AppDispatch,
  setRunning: React.Dispatch<React.SetStateAction<boolean>>,
  setProgress: React.Dispatch<React.SetStateAction<IAutomatProgress | null>>,
): Promise<void> {
  let total = 0;
  for (const record of dataSource) {
    const source = resolveAutomatSource(record);
    total +=
      source.value.length >= 2 ? record.languageType.missingCodes.length : 0;
  }

  setRunning(true);
  const startedAt = Date.now();
  setProgress({ current: 0, total, startedAt });
  let current = 0;

  for (const record of dataSource) {
    const names: ITranslation[] = [];
    const source = resolveAutomatSource(record);

    for (const languageTypeCodeMissing of record.languageType.missingCodes) {
      if (source.value.length >= 2) {
        await new Promise<void>((resolve): void => {
          window.addEventListener(
            'focus',
            async (): Promise<void> => {
              const text: string = await navigator.clipboard.readText();
              const tt: string = text.trim();

              if (tt) {
                names.push({
                  code: languageTypeCodeMissing,
                  value: tt,
                });
              }

              await navigator.clipboard.writeText('');

              resolve();
            },
            { once: true },
          );

          openGoogleTranslateLink(
            source.code,
            languageTypeCodeMissing,
            source.value,
          );
        });

        current++;
        setProgress({ current, total, startedAt });
      }
    }

    if (names.length === 0) {
      continue;
    }

    dispatch(updateValueNames(record.propertyValue.code, names));
  }

  setRunning(false);
  setProgress(null);
}

function ReportPropertyValueLanguages(): React.ReactElement {
  const dispatch = useAppDispatch();
  const reportPropertyValueLanguages = useSelector(
    (s: RootState) => s.Report.propertyValueLanguages,
  );

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [isRunning, setRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<IAutomatProgress | null>(null);
  const [safeModeAll, setSafeModeAll] = useState<boolean>(true);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedRows, setSelectedRows] = useState<DataType[]>([]);

  useAutomatDocumentTitle(isRunning, progress);

  useEffect((): void => {
    if (!reportPropertyValueLanguages) {
      return;
    }

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Property code',
        dataIndex: ['property', 'code'],
        render: (_text: string, record: DataType): React.ReactElement => (
          <Link
            to={`/property/${record.property.code}`}
            openInNewTab
            label={record.property.code}
          />
        ),
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.property.code, b.property.code),
        filters: [
          ...new Set(reportPropertyValueLanguages.map((i) => i.property.code)),
        ]
          .sort()
          .map((code) => ({ text: code, value: code })),
        onFilter: (value, record: DataType): boolean =>
          record.property.code === value,
        filterSearch: true,
        width: '20%',
      },
      {
        title: 'Property value code',
        dataIndex: ['propertyValue', 'code'],
        render: (text: string): string => text,
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.propertyValue.code, b.propertyValue.code),
        width: '20%',
      },
      {
        title: 'Name',
        dataIndex: ['translation', 'names', 0, 'value'],
        render: (_text, record: DataType): React.ReactElement | string => {
          if (record.translation.names.length === 0) {
            const suggestion: string = deriveNameFromCode(
              record.propertyValue.code,
            );

            return (
              <Text type="secondary" italic>
                ~ {suggestion}
              </Text>
            );
          }

          return `[${record.translation.names[0].code}] ${record.translation.names[0].value}`;
        },
        filters: [
          {
            text: 'Has translation',
            value: 'has_translation',
          },
          {
            text: 'Missing translation',
            value: 'missing_translation',
          },
        ],
        onFilter: (value: string, record: DataType): boolean => {
          if (value === 'has_translation') {
            return record.translation.names.length > 0;
          }

          if (value === 'missing_translation') {
            return record.translation.names.length === 0;
          }

          return false;
        },
        width: '20%',
      },
      {
        title: 'Missing languages',
        dataIndex: ['languageType', 'missingCodes'],
        render: (_text, record: DataType): string =>
          record.languageType.missingCodes.join(', '),
        width: '30%',
      },
      {
        title: 'Actions',
        render: (record: DataType): React.ReactElement => {
          const hasTranslation: boolean = record.translation.names.length > 0;

          if (!hasTranslation) {
            const derivedName: string = deriveNameFromCode(
              record.propertyValue.code,
            );
            const derivedNames: ITranslation[] =
              record.languageType.missingCodes.map(
                (code: string): ITranslation => ({ code, value: derivedName }),
              );

            if (safeModeAll) {
              return (
                <Popconfirm
                  placement="bottom"
                  title={`Save "${derivedName}" for all language types?`}
                  onConfirm={(): void => {
                    dispatch(
                      updateValueNames(record.propertyValue.code, derivedNames),
                    );
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary">Save code as name</Button>
                </Popconfirm>
              );
            }

            return (
              <Button
                type="primary"
                danger
                onClick={(): void => {
                  dispatch(
                    updateValueNames(record.propertyValue.code, derivedNames),
                  );
                }}
              >
                Save code as name
              </Button>
            );
          }

          if (safeModeAll) {
            return (
              <Popconfirm
                placement="bottom"
                title="Save as all active language types?"
                onConfirm={(): void => {
                  const names: ITranslation[] =
                    record.languageType.missingCodes.map(
                      (code: string): ITranslation => ({
                        code,
                        value: record.translation.names[0].value,
                      }),
                    );

                  dispatch(updateValueNames(record.propertyValue.code, names));
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button type="primary">Save as all same</Button>
              </Popconfirm>
            );
          }

          return (
            <Button
              type="primary"
              danger
              onClick={(): void => {
                const names: ITranslation[] =
                  record.languageType.missingCodes.map(
                    (code: string): ITranslation => ({
                      code,
                      value: record.translation.names[0].value,
                    }),
                  );

                dispatch(updateValueNames(record.propertyValue.code, names));
              }}
            >
              Save as all same
            </Button>
          );
        },
        width: '10%',
      },
    ];

    setColumns(columnsUE);

    const dataSourceUE = reportPropertyValueLanguages
      .filter((item) => {
        if (filterMode === 'numeric')
          return isNumericCode(item.propertyValue.code);
        if (filterMode === 'non-numeric')
          return !isNumericCode(item.propertyValue.code);
        if (filterMode === 'dimensions')
          return isDimensionCode(item.propertyValue.code);
        return true;
      })
      .map((item) => ({ ...item, key: item.propertyValue.code }));

    setDataSource(dataSourceUE);
  }, [reportPropertyValueLanguages, safeModeAll, dispatch, filterMode]);

  return (
    <>
      <DetailPageHeader
        title="Report property value languages"
        onReload={(): void => {
          dispatch(reportPropertyValueLanguagesLoad());
          setSafeModeAll(true);
        }}
        isReloadDisabled={!reportPropertyValueLanguages}
      />

      <Space size={SPACING.lg} align="start">
        <Button
          type={filterMode === 'all' ? 'primary' : 'default'}
          onClick={(): void => {
            setFilterMode('all');
            dispatch(reportPropertyValueLanguagesLoad());
            setSafeModeAll(true);
          }}
          disabled={!reportPropertyValueLanguages}
        >
          <ReloadOutlined /> All
        </Button>
        <Button
          type={filterMode === 'numeric' ? 'primary' : 'default'}
          onClick={(): void => {
            setFilterMode('numeric');
            dispatch(reportPropertyValueLanguagesLoad());
            setSafeModeAll(true);
          }}
          disabled={!reportPropertyValueLanguages}
        >
          <ReloadOutlined /> Numeric
        </Button>

        <Button
          type={filterMode === 'non-numeric' ? 'primary' : 'default'}
          onClick={(): void => {
            setFilterMode('non-numeric');
            dispatch(reportPropertyValueLanguagesLoad());
            setSafeModeAll(true);
          }}
          disabled={!reportPropertyValueLanguages}
        >
          <ReloadOutlined /> Non-numeric
        </Button>

        <Button
          type={filterMode === 'dimensions' ? 'primary' : 'default'}
          onClick={(): void => {
            setFilterMode('dimensions');
            dispatch(reportPropertyValueLanguagesLoad());
            setSafeModeAll(true);
          }}
          disabled={!reportPropertyValueLanguages}
        >
          <ReloadOutlined /> Dimensions
        </Button>

        <Space size={SPACING.lg} align="start">
          <Button
            type="primary"
            onClick={(): void => {
              startAutomat(dataSource, dispatch, setRunning, setProgress);
            }}
            disabled={
              !reportPropertyValueLanguages ||
              isRunning ||
              dataSource.length === 0
            }
          >
            GT Automat
          </Button>
          <Spin size="small" spinning={isRunning} />
        </Space>

        <Button
          type={safeModeAll ? 'default' : 'primary'}
          danger
          disabled={!reportPropertyValueLanguages}
          onClick={(): void => {
            setSafeModeAll(!safeModeAll);
          }}
        >
          Safe mode all without asking
        </Button>
      </Space>

      <AutomatProgressBar isRunning={isRunning} progress={progress} />

      <Divider />

      <Space size={SPACING.lg} align="start">
        {(() => {
          const withTranslation: DataType[] = selectedRows.filter(
            (r) => r.translation.names.length > 0,
          );
          const withoutTranslation: DataType[] = selectedRows.filter(
            (r) => r.translation.names.length === 0,
          );

          const dispatchSaveAllSame = (): void => {
            for (const record of withTranslation) {
              const names: ITranslation[] =
                record.languageType.missingCodes.map(
                  (code: string): ITranslation => ({
                    code,
                    value: record.translation.names[0].value,
                  }),
                );
              dispatch(updateValueNames(record.propertyValue.code, names));
            }
          };

          const dispatchSaveCodeAsName = (): void => {
            for (const record of withoutTranslation) {
              const derivedName: string = deriveNameFromCode(
                record.propertyValue.code,
              );
              const names: ITranslation[] =
                record.languageType.missingCodes.map(
                  (code: string): ITranslation => ({
                    code,
                    value: derivedName,
                  }),
                );
              dispatch(updateValueNames(record.propertyValue.code, names));
            }
          };

          return (
            <>
              {safeModeAll ? (
                <Popconfirm
                  placement="bottom"
                  title={`Save as all same for ${withTranslation.length} selected?`}
                  onConfirm={dispatchSaveAllSame}
                  okText="Yes"
                  cancelText="No"
                  disabled={withTranslation.length === 0}
                >
                  <Button
                    type="primary"
                    disabled={withTranslation.length === 0}
                  >
                    Save as all same ({withTranslation.length})
                  </Button>
                </Popconfirm>
              ) : (
                <Button
                  type="primary"
                  danger
                  disabled={withTranslation.length === 0}
                  onClick={dispatchSaveAllSame}
                >
                  Save as all same ({withTranslation.length})
                </Button>
              )}

              {safeModeAll ? (
                <Popconfirm
                  placement="bottom"
                  title={`Save code as name for ${withoutTranslation.length} selected?`}
                  onConfirm={dispatchSaveCodeAsName}
                  okText="Yes"
                  cancelText="No"
                  disabled={withoutTranslation.length === 0}
                >
                  <Button
                    type="primary"
                    disabled={withoutTranslation.length === 0}
                  >
                    Save code as name ({withoutTranslation.length})
                  </Button>
                </Popconfirm>
              ) : (
                <Button
                  type="primary"
                  danger
                  disabled={withoutTranslation.length === 0}
                  onClick={dispatchSaveCodeAsName}
                >
                  Save code as name ({withoutTranslation.length})
                </Button>
              )}
            </>
          );
        })()}
      </Space>

      <Divider />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={!reportPropertyValueLanguages}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedRows.map((r) => r.key),
          onChange: (_keys, rows): void => {
            setSelectedRows(rows as DataType[]);
          },
        }}
      />
    </>
  );
}

export default ReportPropertyValueLanguages;
